import React, { useContext, useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import { uploadImage } from '@helpers/api'
import { storagePath } from '@helpers/variables'
import Button from '@components/Button'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
  EXAM_TYPES,
  EXAM_DISPLAY_TYPES,
  EXAM_QUESTION_ORDER_TYPES,
  COLORS,
} from '@helpers/constants'
import Text from '@components/Text'
import { useUpdateExam } from '@hooks/mutations/useUpdateExam'
import ButtonWithLoader from '@components/ButtonWithLoader'
import { nanoid } from 'nanoid'
import { mutate } from 'swr'
import { Single_Book } from '@graphql/queries'
import GridColumns from '@components/GridColumns'
import Icon from '@components/Icon'

const EditExamPanel = () => {
  const [state, dispatch] = useContext(AppContext)
  const {
    app: { currentExam, currentEditBookObject },
  } = state

  const { post, isPosting } = useUpdateExam()

  const {
    register,
    handleSubmit,
    errors,
    reset,
    control,
    formState,
    setValue,
    watch,
    trigger,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: currentExam.name,
      description: currentExam.description,
      additionalDescriptionOne: currentExam.additionalDescriptionOne,
      additionalDescriptionTwo: currentExam.additionalDescriptionTwo,
      content: currentExam.content ? JSON.stringify(currentExam.content) : '{}',
      passingPercentage: currentExam.passingPercentage,
      maxAttempts: currentExam.maxAttempts,
      questionOrderType: currentExam.questionOrderType,
      showAllRightAnswers: currentExam.showAllRightAnswers ? 'yes' : undefined,
      answerDisplayCount: currentExam.answerDisplayCount,
      displayType: currentExam.displayType,
    },
    shouldUnregister: false,
  })

  const { isDirty } = formState

  const [localIsDirty, setLocalIsDirty] = useState(false)

  const onSubmit = async (data) => {
    let fileName = ''

    if (data.image && data.image.length > 0) {
      // User has selected new image, upload it!
      try {
        fileName = await uploadImage(data.image[0])
      } catch (error) {
        console.log(error)
        // show toast here?
      }
    } else if (currentExam.image) {
      fileName = currentExam.image
    }

    const postData = {
      id: currentExam.id,
      name: data.name,
      image: fileName,
      description: data.description,
      additionalDescriptionOne: data.additionalDescriptionOne,
      additionalDescriptionTwo: data.additionalDescriptionTwo,
    }

    if (currentExam.type === parseInt(EXAM_TYPES.QUIZ.value)) {
      postData['content'] = data.content
      postData['passingPercentage'] = parseInt(data.passingPercentage)
      postData['maxAttempts'] = parseInt(data.maxAttempts)
      postData['questionOrderType'] = parseInt(data.questionOrderType)
      postData['showAllRightAnswers'] =
        data.showAllRightAnswers === 'yes' ? true : false
      postData['answerDisplayCount'] = parseInt(data.answerDisplayCount)
      postData['displayType'] = parseInt(data.displayType)
    }

    const res = await post(postData)

    if (res.success) {
      dispatch(closePanel(PANELS.EDIT_EXAM))
    }
  }

  const typeLabel =
    Object.values(EXAM_TYPES).find(
      (examType) => parseInt(examType.value) === currentExam.type
    )?.label || 'Label not found'

  // --- Content wrangling

  const [content, setContent] = useState(JSON.parse(currentExam.content))

  useEffect(() => {
    if (content) {
      const contentAsJson = JSON.stringify(content)
      setValue('content', contentAsJson)
      setLocalIsDirty(true)
    }
  }, [content])

  // --- Questions
  const [collapsedQuestionIds, setCollapsedQuestionIds] = useState([])
  const toggleCollapsedQuestion = (id) => {
    setCollapsedQuestionIds(
      (prev) =>
        prev.includes(id)
          ? prev.filter((questionId) => questionId !== id) // Remove if already collapsed
          : [...prev, id] // Add if not collapsed
    )
  }

  const { questions = [] } = content || {}

  /**
   * Add new question
   */
  const addNewQuestion = () => {
    setContent({
      ...content,
      questions: [
        ...questions,
        {
          id: nanoid(8),
          question: '{}',
          answers: [
            { id: nanoid(8), text: '{}', correct: true, textArea: '{}' },
            { id: nanoid(8), text: '{}', correct: false, textArea: '{}' },
            { id: nanoid(8), text: '{}', correct: false, textArea: '{}' },
            { id: nanoid(8), text: '{}', correct: false, textArea: '{}' },
            { id: nanoid(8), text: '{}', correct: false, textArea: '{}' },
          ],
        },
      ],
    })
  }

  /**
   * Update question
   * @param {*} questionIndex
   * @param {*} key
   * @param {*} value
   * @param {*} answerIndex
   */
  const updateQuestion = (questionIndex, key, value, answerIndex = false) => {
    const newQuestions = questions.map((question, qKey) => {
      if (questionIndex === qKey) {
        // If updating answer under question?
        if (answerIndex !== false) {
          question.answers = question.answers.map((answer, aKey) => {
            // Reset correct (not applicable now that we have checkboxes)
            // if (key === 'correct') {
            //   answer.correct = false
            //   answer.textArea = '{}'
            // }

            // Check if the user is trying to set 'correct' to false
            if (key === 'correct' && !value) {
              const correctAnswers = question.answers.filter((a) => a.correct)

              // If there's only one correct answer and it's the one being toggled, dont allow it
              if (correctAnswers.length === 1 && aKey === answerIndex) {
                return answer // Don't allow setting 'correct' to false
              }
            }

            if (answerIndex === aKey) {
              answer[key] = value
            }
            return answer
          })
        } else {
          question[key] = value
        }
      }
      return question
    })
    setContent({
      ...content,
      questions: newQuestions,
    })
  }

  /**
   * Remove question
   * @param {*} questionIndex
   */
  const removeQuestion = (questionIndex) => {
    setContent({
      ...content,
      questions: questions.filter((q, k) => k !== questionIndex),
    })
  }

  const moveQuestion = (fromIndex, toIndex) => {
    // setScrollTo(ref.current.parentNode.scrollTop)

    const element = questions[fromIndex]
    questions.splice(fromIndex, 1)
    questions.splice(toIndex, 0, element)

    setContent({
      ...content,
      questions: [...questions],
    })
  }

  /**
   * Add new answer
   * @param {*} questionIndex
   */
  const addNewAnswer = (questionIndex) => {
    const answers = content['questions'][questionIndex].answers
    const updatedAnswers = [
      ...answers,
      { id: nanoid(8), text: '{}', correct: false, textArea: '{}' },
    ]

    const updatedQuestions = [...questions]
    updatedQuestions[questionIndex].answers = updatedAnswers

    setContent({
      ...content,
      questions: [...updatedQuestions],
    })
  }

  /**
   * Remove an answer
   * @param {*} questionIndex
   * @param {*} answerIndex
   */
  const removeAnswer = (questionIndex, answerIndex) => {
    const updatedAnswers = content['questions'][questionIndex].answers.filter(
      (answer, index) => index !== answerIndex
    )

    const updatedQuestions = [...questions]
    updatedQuestions[questionIndex].answers = updatedAnswers

    setContent({
      ...content,
      questions: [...updatedQuestions],
    })
  }

  return (
    <>
      <div className={'flex justify-between items-center'}>
        <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.MEDIUM}>
          {typeLabel}
        </Heading>

        <Button
          handleClick={() => {
            dispatch(closePanel(PANELS.EDIT_EXAM))
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        ></Button>
      </div>

      <Divider size={DIVIDER_SIZE.SMALL} />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Label htmlFor='name'>Namn på testet</Form.Label>
        <Form.Input
          type={'text'}
          name='name'
          id='name'
          ref={register({ required: true })}
          placeholder='Titel'
        />
        {errors.name && <Form.ErrorMessage>Titel behövs</Form.ErrorMessage>}

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='description'>Inledande text</Form.Label>
        <Controller
          control={control}
          name='description'
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Editor
              content={currentExam.description}
              handleChange={(value) => onChange(value)}
              elementId={`edit-exam-description-${currentExam.id}`}
              // placeholder='Beskrivning'
            />
          )}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='image'>Bild</Form.Label>
        <Form.ImageUpload
          name='image'
          id='image'
          ref={register()}
          imagePreviewUrl={
            currentExam.image ? `${storagePath}/${currentExam.image}` : null
          }
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='additionalDescriptionOne'>
          &quot;Uppgift&quot;-text
        </Form.Label>
        <Controller
          control={control}
          name='additionalDescriptionOne'
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Editor
              content={currentExam.additionalDescriptionOne}
              handleChange={(value) => onChange(value)}
              elementId={`edit-exam-additional-description-one-${currentExam.id}`}
              // placeholder='Beskrivning'
            />
          )}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />

        <Form.Label htmlFor='additionalDescriptionTwo'>
          &quot;Bra att veta&quot;-text
        </Form.Label>
        <Controller
          control={control}
          name='additionalDescriptionTwo'
          render={(
            { onChange, onBlur, value, name, ref },
            { invalid, isTouched, isDirty }
          ) => (
            <Form.Editor
              content={currentExam.additionalDescriptionTwo}
              handleChange={(value) => onChange(value)}
              elementId={`edit-exam-additional-description-two-${currentExam.id}`}
              // placeholder='Beskrivning'
            />
          )}
        />

        {currentExam.type === parseInt(EXAM_TYPES.QUIZ.value) && (
          <>
            <Divider size={DIVIDER_SIZE.SMALL} />

            <GridColumns cols={2}>
              <div>
                <Form.Label htmlFor='passingPercentage'>Procent*</Form.Label>
                <Form.Input
                  type={'number'}
                  name='passingPercentage'
                  id='passingPercentage'
                  ref={register({ required: true })}
                />
                {errors.passingPercentage && (
                  <Form.ErrorMessage>Procent behövs</Form.ErrorMessage>
                )}
              </div>
              <div>
                <Form.Label htmlFor='maxAttempts'>Antal försök*</Form.Label>
                <Form.Input
                  type={'number'}
                  name='maxAttempts'
                  id='maxAttempts'
                  ref={register({
                    required: true,
                    min: {
                      value: 1,
                      message: 'Antal försök får inte vara mindre än 1',
                    },
                  })}
                />
                {errors.maxAttempts && (
                  <Form.ErrorMessage>
                    {errors.maxAttempts.message ?? 'Antal försök behövs'}
                  </Form.ErrorMessage>
                )}
              </div>

              <div>
                <Form.Label htmlFor='questionOrderType'>
                  Frågeordning
                </Form.Label>
                <Form.Select
                  name='questionOrderType'
                  id='questionOrderType'
                  ref={register({ required: true })}
                  options={Object.values(EXAM_QUESTION_ORDER_TYPES).map(
                    (orderType, index) => {
                      const { label, value } = orderType
                      return { value: value, name: label }
                    }
                  )}
                />
                {errors.questionOrderType && (
                  <Form.ErrorMessage>Frågeordning behövs</Form.ErrorMessage>
                )}
              </div>
              <div>
                <Form.Label htmlFor='answerDisplayCount'>
                  Antal alternativ
                </Form.Label>
                <Form.Select
                  name='answerDisplayCount'
                  id='answerDisplayCount'
                  ref={register({ required: true })}
                  options={[
                    { value: '3', name: '3' },
                    { value: '4', name: '4' },
                    { value: '5', name: '5' },
                  ]}
                />
                {errors.answerDisplayCount && (
                  <Form.ErrorMessage>Antal alternativ behövs</Form.ErrorMessage>
                )}
              </div>

              <div>
                <Form.Label htmlFor='displayType'>Visningsläge</Form.Label>
                <Form.Select
                  name='displayType'
                  id='displayType'
                  ref={register({ required: true })}
                  options={Object.values(EXAM_DISPLAY_TYPES).map(
                    (orderType, index) => {
                      const { label, value } = orderType
                      return { value: value, name: label }
                    }
                  )}
                />
                {errors.displayType && (
                  <Form.ErrorMessage>Visningsläge behövs</Form.ErrorMessage>
                )}
              </div>
              <div>
                <Form.Label htmlFor='showAllRightAnswers'>
                  Visa alla rätta svar
                </Form.Label>
                <Form.Checkbox
                  id='showAllRightAnswers'
                  label='Ja'
                  value={'yes'}
                  name='showAllRightAnswers'
                  ref={register()}
                />
              </div>
            </GridColumns>

            <Divider size={DIVIDER_SIZE.SMALL} />

            {/* --- Questions start --- */}

            {questions && questions.length > 0 && (
              <>
                <Divider size={DIVIDER_SIZE.SMALL} />

                <ul>
                  {questions.map((currentQuestion, questionIndex) => {
                    const { id: questionId } = currentQuestion
                    const { question, answers = [] } = currentQuestion

                    const isCollapsed =
                      collapsedQuestionIds.includes(questionId)

                    return (
                      <li key={questionId}>
                        {/* collapsedQuestionIds */}
                        <div className='relative'>
                          <Heading headingLevel={HEADING_TYPE.H3}>
                            Fråga {questionIndex + 1}
                          </Heading>
                          <Divider size={DIVIDER_SIZE.LINE} line />
                          <Button
                            classes='absolute top-0 right-0 pr-1'
                            handleClick={(e) => {
                              e.preventDefault()
                              toggleCollapsedQuestion(questionId)
                            }}
                          >
                            <Icon
                              icon='ChevronUp'
                              iconClasses='w-7 h-7'
                              rotate={isCollapsed ? 180 : 0}
                            />
                          </Button>
                        </div>

                        <Divider size={DIVIDER_SIZE.SMALL} />

                        {!isCollapsed && (
                          <>
                            <Form.Editor
                              content={question}
                              handleChange={(value) =>
                                updateQuestion(questionIndex, 'question', value)
                              }
                              elementId={`quiz-question-${questionId}`}
                            />

                            <Divider size={DIVIDER_SIZE.SMALL} />
                            <p>
                              Lägg till minst fem svarsalternativ, där minst ett
                              av dem är rätt.
                            </p>
                            <Divider size={DIVIDER_SIZE.SMALL} />

                            {/* Answers start */}

                            <ul>
                              {answers.map((currentAnswer, answerIndex) => {
                                if (!currentAnswer.id) {
                                  updateQuestion(
                                    questionIndex,
                                    'id',
                                    nanoid(8),
                                    answerIndex
                                  )

                                  return null
                                }
                                const {
                                  correct,
                                  text,
                                  textArea,
                                  id: answerId,
                                } = currentAnswer

                                const removeAllowed = answers.length > 5

                                return (
                                  <div key={answerId}>
                                    <li className={'flex items-baseline'}>
                                      <Form.Checkbox
                                        name={`question-answer-${questionId}`}
                                        checked={correct}
                                        onChange={(e) =>
                                          updateQuestion(
                                            questionIndex,
                                            'correct',
                                            e.target.checked,
                                            answerIndex
                                          )
                                        }
                                      />

                                      <div className={'flex flex-col w-full'}>
                                        <div
                                          className={
                                            'flex justify-between items-center'
                                          }
                                        >
                                          <Form.Label>
                                            Svarsalternativ {answerIndex + 1}
                                          </Form.Label>

                                          <Button
                                            classes='pr-1'
                                            handleClick={(e) => {
                                              e.preventDefault()

                                              if (removeAllowed) {
                                                removeAnswer(
                                                  questionIndex,
                                                  answerIndex
                                                )
                                              }
                                            }}
                                          >
                                            <Icon
                                              icon='TrashCan'
                                              iconClasses={`w-5 h-5 ${
                                                !removeAllowed
                                                  ? 'opacity-30'
                                                  : ''
                                              }`}
                                            />
                                          </Button>
                                        </div>

                                        <Form.Editor
                                          content={text}
                                          handleChange={(value) =>
                                            updateQuestion(
                                              questionIndex,
                                              'text',
                                              value,
                                              answerIndex
                                            )
                                          }
                                          elementId={`quiz-answer-${answerId}`}
                                        />

                                        {correct && (
                                          <>
                                            <Divider
                                              size={DIVIDER_SIZE.SMALL}
                                            />
                                            <Form.Editor
                                              content={textArea}
                                              handleChange={(value) =>
                                                updateQuestion(
                                                  questionIndex,
                                                  'textArea',
                                                  value,
                                                  answerIndex
                                                )
                                              }
                                              placeholder={'Förklaring'}
                                              elementId={`quiz-textArea-${answerId}`}
                                            />
                                          </>
                                        )}

                                        <Divider size={DIVIDER_SIZE.SMALL} />
                                      </div>
                                    </li>
                                  </div>
                                )
                              })}
                            </ul>

                            <Button
                              handleClick={(e) => {
                                e.preventDefault()
                                addNewAnswer(questionIndex)
                              }}
                              type={BUTTON_TYPE.PRIMARY}
                            >
                              Lägg till ett svarsalternativ
                            </Button>

                            {/* Answers end */}

                            <Divider size={DIVIDER_SIZE.SMALL} />
                            <Button
                              type={BUTTON_TYPE.PRIMARY}
                              handleClick={(e) => {
                                e.preventDefault()
                                removeQuestion(questionIndex)
                              }}
                              marginRight
                            >
                              Ta bort fråga
                            </Button>
                            <Button
                              type={BUTTON_TYPE.PRIMARY}
                              disabled={questionIndex === 0}
                              handleClick={(e) => {
                                e.preventDefault()
                                moveQuestion(questionIndex, questionIndex - 1)
                              }}
                              marginRight
                            >
                              Flytta upp
                            </Button>
                            <Button
                              type={BUTTON_TYPE.PRIMARY}
                              disabled={questionIndex + 1 === questions.length}
                              handleClick={(e) => {
                                e.preventDefault()
                                moveQuestion(questionIndex, questionIndex + 1)
                              }}
                            >
                              Flytta ner
                            </Button>
                            <Divider
                              size={
                                questionIndex + 1 === questions.length
                                  ? DIVIDER_SIZE.SMALL
                                  : DIVIDER_SIZE.MEDIUM
                              }
                            />
                          </>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </>
            )}

            {/* Questions end */}

            <Divider size={DIVIDER_SIZE.SMALL} />

            <Button
              handleClick={(e) => {
                e.preventDefault()
                addNewQuestion()
              }}
              type={BUTTON_TYPE.PRIMARY}
            >
              Lägg till en fråga
            </Button>
          </>
        )}

        <Divider size={DIVIDER_SIZE.BASE} />

        <ButtonWithLoader
          type={BUTTON_TYPE.PRIMARY}
          isLoading={isPosting}
          disabledText={'Sparat'}
          enabledText={'Spara test'}
          disabled={isPosting || (!localIsDirty && !isDirty)}
        />

        <Divider size={DIVIDER_SIZE.SMALL} />
      </Form>
    </>
  )
}

export default EditExamPanel
